export type CsvColumn<T> = {
  /** Column header text shown in the first CSV row. */
  header: string;
  /** Extracts the cell value for this column from a row object. */
  value: (row: T) => unknown;
};

export type BuildCsvOptions = {
  /** Field delimiter. Default: "," */
  delimiter?: string;
  /** Line break sequence. Default: "\r\n" (Excel-friendly) */
  newline?: string;
  /** Add UTF-8 BOM so Excel opens UTF-8 CSVs correctly. Default: true */
  includeBom?: boolean;
};

function normalizeCell(value: unknown): string {
  // Normalize supported JS values into a string for CSV output.
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function escapeCell(value: string, delimiter: string): string {
  // Escape a cell according to RFC4180-ish rules: quote if needed and double internal quotes.
  const mustQuote =
    value.includes('"') || value.includes('\n') || value.includes('\r') || value.includes(delimiter);

  if (!mustQuote) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Build a CSV string from typed rows + column definitions.
 * - Handles quoting/escaping
 * - Uses CRLF by default (Excel-friendly)
 * - Adds UTF-8 BOM by default (Excel-friendly)
 */
export function buildCsv<T>(columns: CsvColumn<T>[], rows: T[], options: BuildCsvOptions = {}): string {
  const delimiter = options.delimiter ?? ',';
  const newline = options.newline ?? '\r\n';
  const includeBom = options.includeBom ?? true;

  const headerLine = columns.map((c) => escapeCell(normalizeCell(c.header), delimiter)).join(delimiter);

  const bodyLines = rows.map((row) =>
    columns
      .map((c) => {
        const raw = normalizeCell(c.value(row));
        return escapeCell(raw, delimiter);
      })
      .join(delimiter),
  );

  const csv = [headerLine, ...bodyLines].join(newline);
  return includeBom ? `\uFEFF${csv}` : csv;
}

/**
 * Trigger a client-side download of a CSV string.
 * Note: call `buildCsv` first to generate the CSV content.
 */
export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  try {
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}


